import React from 'react';
import { Activity, Clock, FileText, UserPlus, FlaskConical, MapPin, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';

interface CrmActivity {
  id: string;
  text: string;
  time: string;
  type: string;
}

interface RecentCrmActivityFeedProps {
  activities: CrmActivity[];
}

export const RecentCrmActivityFeed: React.FC<RecentCrmActivityFeedProps> = ({ activities }) => {
  const navigate = useNavigate();

  const getIcon = (type: string) => {
    switch (type) {
      case 'quotation':
        return <FileText className="h-3.5 w-3.5 text-primary" />;
      case 'lead':
        return <UserPlus className="h-3.5 w-3.5 text-info-text" />;
      case 'sample':
        return <FlaskConical className="h-3.5 w-3.5 text-warning-text" />;
      case 'visit':
        return <MapPin className="h-3.5 w-3.5 text-success-text" />;
      case 'order':
        return <ShoppingBag className="h-3.5 w-3.5 text-info-text" />;
      default:
        return <Activity className="h-3.5 w-3.5 text-on-surface-variant" />;
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
              <Activity className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Recent CRM Activity
              </span>
              <div className="text-data-xs text-on-surface-subtle">Live log of employee actions & updates</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(ROUTES.activity)}
            className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
          >
            <span>Audit Log</span>
          </button>
        </div>

        {/* Activity Feed List */}
        {activities.length === 0 ? (
          <div className="my-6 text-center text-body-sm text-on-surface-subtle py-4">
            No recent activity recorded yet today.
          </div>
        ) : (
          <div className="my-2 flex flex-col divide-y divide-border">
            {activities.map((act) => (
              <div key={act.id} className="flex items-start gap-3 py-2.5 px-1 transition hover:bg-surface-variant rounded-lg">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-surface-container shrink-0 mt-0.5">
                  {getIcon(act.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-body-sm font-medium text-on-surface leading-snug">{act.text}</p>
                  <div className="mt-1 flex items-center gap-1 text-data-xs text-on-surface-subtle">
                    <Clock className="h-3 w-3" />
                    <span>{act.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Activity Stream:</span>
        <span className="font-semibold text-on-surface-variant">Audit trail encrypted & logged</span>
      </div>
    </div>
  );
};
